import React, { useState, useEffect, useCallback } from 'react';
import { Box, Paper, Typography, Button, Chip, Alert, CircularProgress, Avatar, Stack } from '@mui/material';
import { Groups as ClientsIcon, HourglassEmpty as PendingIcon, Business as BusinessIcon } from '@mui/icons-material';
import ApiService from '../../../services/api.service';
import { err, useConnectionCap, ConnectionsHeader } from './shared';
import { can } from '../../../utils/roleUtils';

const rowCard = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
  p: 1.25, mb: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
  bgcolor: 'background.paper', transition: 'box-shadow .15s ease', '&:last-of-type': { mb: 0 },
  '&:hover': { boxShadow: 1 },
};
const sectionPaper = { p: 2, mb: 2, borderRadius: 2 };

const ClientsPage = ({ currentUser }) => {
  const canWrite = can(currentUser, 'connections', 'write');
  const { cap, reloadCap } = useConnectionCap(true);
  const [connections, setConnections] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [conns, pend] = await Promise.all([
        ApiService.getTenantConnections(),
        ApiService.getPendingConnections(),
      ]);
      setConnections(conns?.data?.data || []);
      setPending(pend?.data?.data || []);
      await reloadCap();
    } catch (e) {
      setError(err(e, 'Failed to load clients'));
    } finally { setLoading(false); }
  }, [reloadCap]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try { await ApiService.approveTenantConnection(id); setSuccess('Connection approved'); load(); }
    catch (e) { setError(err(e, 'Failed to approve')); }
  };
  const reject = async (id) => {
    try { await ApiService.rejectTenantConnection(id, ''); setSuccess('Request rejected'); load(); }
    catch (e) { setError(err(e, 'Failed to reject')); }
  };
  const disconnect = async (id) => {
    if (!window.confirm('Disconnect this client? In-flight work orders continue but no new handoffs.')) return;
    try { await ApiService.disconnectTenantConnection(id, ''); setSuccess('Disconnected'); load(); }
    catch (e) { setError(err(e, 'Failed to disconnect')); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  const activeClients = connections.filter((c) => c.status === 'APPROVED');

  return (
    <Box sx={{ p: 3 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <ConnectionsHeader icon={<ClientsIcon />} title="Client Typing Centers" cap={cap} />

      {cap?.reached && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You've reached your plan's client limit ({cap.used}/{cap.limit}). Upgrade your plan to accept more.
        </Alert>
      )}

      <Paper sx={sectionPaper}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Pending Requests</Typography>
        {pending.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No pending requests.</Typography>
        ) : pending.map((c) => (
          <Box key={c.id} sx={{ ...rowCard, borderLeft: '3px solid', borderLeftColor: 'warning.main' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: 'warning.light' }}><PendingIcon sx={{ fontSize: 19 }} /></Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{c.client_name} <Typography component="span" variant="caption" color="text.secondary">({c.client_code})</Typography></Typography>
                {c.notes && <Typography variant="caption" color="text.secondary" display="block" noWrap>{c.notes}</Typography>}
              </Box>
            </Box>
            {canWrite && (
              <Stack direction="row" spacing={1}>
                <Button size="small" color="error" onClick={() => reject(c.id)} sx={{ textTransform: 'none' }}>Reject</Button>
                <Button size="small" variant="contained" color="success" disabled={cap?.reached} onClick={() => approve(c.id)} sx={{ textTransform: 'none', fontWeight: 600 }}>Approve</Button>
              </Stack>
            )}
          </Box>
        ))}
      </Paper>

      <Paper sx={sectionPaper}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Active Clients</Typography>
        {activeClients.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No active clients.</Typography>
        ) : activeClients.map((c, idx) => (
          <Box key={c.id} sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 1.25,
            borderBottom: idx < activeClients.length - 1 ? '1px solid' : 'none', borderColor: 'divider',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
              <BusinessIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="body2" fontWeight={600} noWrap>{c.client_name}</Typography>
              <Typography variant="caption" color="text.secondary">{c.client_code}</Typography>
              {c.is_promoted && <Chip label="Origin" size="small" color="secondary" sx={{ height: 18, fontSize: '0.65rem' }} />}
            </Box>
            {!c.is_promoted && canWrite && (
              <Button size="small" variant="outlined" color="error" onClick={() => disconnect(c.id)} sx={{ textTransform: 'none', flexShrink: 0 }}>Disconnect</Button>
            )}
          </Box>
        ))}
      </Paper>
    </Box>
  );
};

export default ClientsPage;
